import {Component} from "@angular/core";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import * as rasterizeHTML from "rasterizehtml"; // eslint-disable-line import/no-unresolved
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {SupportResponseModel} from "./support-response.model";
import {handleErrors} from "../misc/handle-errors.decorator";

export interface SupportModel {
  currentUrl: string;
}

function b64EncodeUnicode(str) {
  // from https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
  // first we use encodeURIComponent to get percent-encoded UTF-8,
  // then we convert the percent encodings into raw bytes which
  // can be fed into btoa.
  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (m, p1) => String.fromCharCode(parseInt(p1, 16))));
}

@Component({
  templateUrl: "./support.template.pug",
})
export class SupportComponent extends DialogComponent<SupportModel, {}> implements SupportModel {
  title: string;
  message: string;
  projectName: string;
  severity: string = "17033";
  region: string = "";
  imageData: string;
  imageTrustUrl: SafeUrl;
  image: any;
  svg: any;
  currentUrl: string;
  error: Error;

  createdDefectId: string;
  createdDefectUrl: string;

  showForm: boolean = true;
  showSpinner: boolean = false;
  showResult: boolean = false;

  static showSupportModal(dialogService: DialogService): void {
    const currentUrl = document.location.href;
    dialogService.addDialog(SupportComponent, {currentUrl}, {backdropColor: "rgba(0, 0, 0, 0.5)"});
  }

  private static persistInputValues(document: Document) {
    const inputs = Array.from(document.querySelectorAll("input")) as HTMLInputElement[];
    const textareas = Array.from(document.querySelectorAll("textarea")) as HTMLTextAreaElement[];
    const selects = Array.from(document.querySelectorAll("select")) as HTMLSelectElement[];

    for (const input of inputs) {
      switch (input.type) {
        case "checkbox":
        case "radio":
          input.setAttribute("checked", input.checked ? "checked" : null);
          break;
        default:
          input.setAttribute("value", input.value);
          break;
      }
    }

    for (const textarea of textareas) {
      textarea.setAttribute("textContent", textarea.value);
    }

    for (const selectable of selects) {
      const options = Array.from(selectable.options) as HTMLOptionElement[];
      const selectedOptions = Array.from(selectable.selectedOptions) as HTMLOptionElement[];
      for (const option of options) {
        option.setAttribute("selected", null);
        for (const selectedOption of selectedOptions) {
          if (selectedOption.value === option.value) {
            option.setAttribute("selected", "selected");
          }
        }
      }
    }
  }

  addScreenshot(): void {
    const clone = document.cloneNode(true) as HTMLDocument;
    SupportComponent.persistInputValues(clone);

    const html = clone.getElementsByTagName("html")[0] as HTMLHtmlElement;
    html.className = `${html.className} screenshot-assist`;

    rasterizeHTML.drawDocument(clone, undefined, {width: `${window.innerWidth}`})
      .then(
        ({image, svg}) => {
          this.image = image;
          this.svg = svg;

          const canvas: HTMLCanvasElement = document.createElement("canvas") as HTMLCanvasElement;
          canvas.width = image.width;
          canvas.height = image.height;
          canvas.getContext("2d").drawImage(image, 0, 0);
          this.imageData = canvas.toDataURL("image/png");
          this.imageTrustUrl = this.domSanitizer.bypassSecurityTrustUrl(this.imageData);
        },
        (error) => {
          console.error(error);
        },
      );
  }

  removeScreenshot(): void {
    this.imageTrustUrl = undefined;
  }

  @handleErrors()
  async submit(): Promise<any> {
    if (!this.title || !this.message || !this.severity || !this.region) return;

    this.createdDefectId = undefined;
    this.createdDefectUrl = undefined;
    this.showForm = false;
    this.showSpinner = true;
    this.showResult = false;

    const body = {
      title: this.title,
      message: this.message,
      projectName: this.projectName,
      severity: this.severity,
      region: this.region,
      image: this.imageData ? this.imageData.replace(/^data:image\/png;base64,/, "") : undefined,
      svg: this.svg ? b64EncodeUnicode(this.svg) : undefined,
      url: window["location"]["hash"],
    };

    try {
      const result = await this.http.post<SupportResponseModel>("/api/support", body)
        .pipe(map(json => new SupportResponseModel(json)))
        .toPromise();

      this.createdDefectId = result.defectId;
      this.createdDefectUrl = result.defectUrl;

      this.showForm = false;
      this.showSpinner = false;
      this.showResult = true;
    } catch (error) {
      this.showForm = true;
      this.showSpinner = false;
      this.showResult = false;

      throw error;
    }
  }

  constructor(
    public dialogService: DialogService,
    public http: HttpClient,
    private domSanitizer: DomSanitizer,
  ) {
    super(dialogService);
  }
}
